import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/use-locale';
import { useQuery } from '@tanstack/react-query';
import pkg from '../../../package.json';
import { useTheme } from '@/components/theme-provider';
import {
  Building2,
  HardHat,
  LayoutDashboard,
  Receipt,
  Wrench,
  CreditCard,
  BarChart3,
  Building,
  Home,
  Bell,
  Vote,
  CalendarCheck,
  LogOut,
  ChevronsUpDown,
  Car,
  Plus,
  Landmark,
  Check,
  ShieldCheck,
  UserCircle,
  Users,
  Menu,
  X,
  Search,
  Settings,
  ScrollText,
  Coins,
  Languages,
  Eye,
  EyeOff,
  Sun,
  Moon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderClosed,
  KeyRound,
} from '@/components/icons';
import { cn } from '@/lib/utils';
import {
  docsPrefetchHandlers,
  type DocsPrefetchHandlers,
} from '@/lib/docs-prefetch';
import { useAuth } from '@/contexts/auth-context';
import { isActingContractor, isActingResident } from '@/lib/user-roles';
import { useProperty } from '@/contexts/property-context';

import { Tooltip } from '@/components/common/Tooltip';
import { usePrivacyMode } from '@/hooks/use-privacy-mode';
import { Badge } from '@/components/common/Badge';
import { ImpersonationBanner } from '@/components/common/ImpersonationBanner';
import { BroadcastBanner } from '@/components/common/BroadcastBanner';
import { WorkspaceSuspendedBanner } from '@/components/common/WorkspaceSuspendedBanner';
import { TrialBanner } from '@/components/layout/TrialBanner';
import { WorkspaceSwitcher } from '@/components/common/WorkspaceSwitcher';
import { ResidentScopeSwitcher } from '@/components/common/ResidentScopeSwitcher';
import { useListboxKeyboard } from '@/hooks/use-listbox-keyboard';
import { useMyWorkspaces } from '@/hooks/use-workspaces';
import { api } from '@/lib/api';
import { contractorApi } from '@/lib/contractor-api';
const CommandPalette = React.lazy(() =>
  import('@/components/common/CommandPalette').then((m) => ({ default: m.CommandPalette }))
);

// Stable identity for query results that have not resolved yet: an inline `[]`
// default is a new array on every render, which defeats every dependent useMemo.
const EMPTY_LIST: never[] = [];

// Built once at module scope: the handlers are stateless and the underlying
// prefetch is memoised, so every navigation group can share this object without
// re-running it on each render.
const DOCS_PREFETCH_HANDLERS = docsPrefetchHandlers();


interface NavGroup {
  label: string;
  items: {
    name: string;
    path: string;
    icon: React.ElementType;
    badge?: string;
    end?: boolean;
    module?: string;
    /**
     * Warms a route chunk on hover/focus/touch. Applied to routes that are a
     * `React.lazy` waterfall, where a cold click would otherwise appear dead.
     */
    prefetch?: DocsPrefetchHandlers;
  }[];
}

export const Shell: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, isImpersonating: _isImpersonating, appMode, appModeResolved, setAppMode } = useAuth();
  const { properties, selectedPropertyId, selectedProperty, setSelectedPropertyId, hasModule, isReadOnly, residentMode, isResidentScope, setResidentMode, activeWorkspaceId, selectedUnitId, setSelectedUnitId } = useProperty();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { privacyMode, togglePrivacyMode } = usePrivacyMode();
  const { data: myWorkspaces = EMPTY_LIST } = useMyWorkspaces();
  const activeWsId = activeWorkspaceId;
  const activeMembership = myWorkspaces.find((m) => m.workspace_id === activeWsId) || myWorkspaces[0];
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/app';
  const isTenancyRoute = location.pathname.startsWith('/tenancy');

  const isDark = (resolvedTheme || theme) === 'dark';

  const canManage = Boolean(user?.role === 'superadmin' || user?.role === 'manager');
  const hasProperties = properties.length > 0;
  
  const effectiveAppMode =
    appMode ||
    (user?.role === 'superadmin'
      ? 'superadmin'
      : isActingContractor(user, { appMode, residentMode })
      ? 'contractor'
      : isActingResident(user, { appMode, residentMode, isResidentScope })
      ? 'home'
      : 'manage');
  const isSuperadmin = effectiveAppMode === 'superadmin';
  const isContractor = effectiveAppMode === 'contractor';
  const residentExperience = effectiveAppMode === 'home';
  const manageChrome = effectiveAppMode === 'manage' || effectiveAppMode === 'superadmin';

  const { data: contractorCompany, isLoading: contractorCompanyLoading } = useQuery({
    queryKey: ['contractor-company'],
    queryFn: contractorApi.getCompany,
    enabled: isContractor,
  });
  // Default to non-admin while the company role is still loading so technicians
  // never flash admin-only groups (BUSINESS/COMPANY).
  const isContractorAdmin =
    !contractorCompanyLoading &&
    (!contractorCompany || contractorCompany.current_user_role !== 'technician');

  const myUnitsScope = appMode === 'home' || residentMode === 'home' ? 'resident' : 'manage';
  const { data: myUnits = EMPTY_LIST, isLoading: myUnitsLoading } = useQuery<{
    unit_id: string;
    unit_number?: string;
    property_id: string;
    relationship?: string;
    financial_access?: boolean;
    /** Owner opt-in for the tenancy_lease module on this unit (Area 3 D7). */
    tenancy_enabled?: boolean;
  }[]>({
    queryKey: ['portal-my-units', myUnitsScope],
    queryFn: async () => {
      const res = await api.get(
        myUnitsScope === 'resident' ? '/portal/my-units?scope=resident' : '/portal/my-units'
      );
      return res.data;
    },
    enabled: Boolean(!isContractor && (residentExperience || isTenancyRoute)),
  });

  const scopedUnits = useMemo(() => {
    const list = selectedProperty
      ? myUnits.filter((u) => u.property_id === selectedProperty.id)
      : myUnits;
    return [...list].sort((a, b) =>
      (a.unit_number || '').localeCompare(b.unit_number || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }, [selectedProperty, myUnits]);

  const scopedUnitsCount = scopedUnits.length;

  const activeManageUnit = useMemo(() => {
    if (selectedUnitId) {
      const found = scopedUnits.find((u) => u.unit_id === selectedUnitId);
      if (found) return found;
    }
    return scopedUnits[0] || null;
  }, [scopedUnits, selectedUnitId]);

  const activeResidentUnit = useMemo(() => {
    if (!residentExperience) return null;
    const scoped = selectedProperty
      ? myUnits.filter((u) => u.property_id === selectedProperty.id)
      : myUnits;
    if (selectedUnitId) {
      const found = scoped.find((u) => u.unit_id === selectedUnitId);
      if (found) return found;
    }
    return scoped[0] || null;
  }, [residentExperience, selectedProperty, myUnits, selectedUnitId]);

  // While the unit list is still loading the relationship is unknown: default
  // both flags to false so renters never flash owner-only entries (Finances,
  // Polls) before the query resolves.
  const residentUnitsReady = !myUnitsLoading;
  const residentHasFinancialAccess = Boolean(
    !residentExperience ||
    (residentUnitsReady &&
      (!activeResidentUnit ||
        (activeResidentUnit.financial_access !== false &&
          activeResidentUnit.relationship !== 'renter')))
  );

  const residentCanVote = Boolean(
    !residentExperience ||
    (residentUnitsReady &&
      (!activeResidentUnit || activeResidentUnit.relationship !== 'renter'))
  );

  // Determine if there are scope controls to display on desktop:
  // - Contractors have no scope controls
  // - Single-hat residents only have switcher controls if they have >= 2 properties or > 1 unit
  // - Management users (staff) always have the workspace / property selector
  const hasResidentScopeControls = properties.length >= 2 || scopedUnitsCount > 1;
  const hasDesktopScopeControls = isContractor ? false : residentExperience ? hasResidentScopeControls : true;

  const navigationGroups: NavGroup[] = useMemo(() => {
    if (isContractor) {
      return [
        {
          label: '',
          items: [
            { name: t('nav.contractor_dashboard', 'Dashboard'), path: '/contractor/dashboard', icon: LayoutDashboard, end: true },
          ],
        },
        ...(isContractorAdmin
          ? [
            {
              label: t('nav.contractor_company', 'COMPANY'),
              items: [
                { name: t('nav.contractor_profile', 'Company Profile'), path: '/contractor/profile', icon: HardHat },
                { name: t('nav.contractor_team', 'Team'), path: '/contractor/team', icon: Users },
              ],
            },
          ]
          : []),
        {
          label: t('nav.contractor_work', 'WORK'),
          items: [
            { name: t('nav.contractor_jobs', 'Work Orders'), path: '/contractor/jobs', icon: Wrench },
            { name: t('nav.contractor_schedule', 'Schedule'), path: '/contractor/schedule', icon: CalendarCheck },
          ],
        },
        ...(isContractorAdmin
          ? [
            {
              label: t('nav.contractor_business', 'BUSINESS'),
              items: [
                { name: t('nav.contractor_invoices', 'Quotes & Invoices'), path: '/contractor/invoices', icon: Receipt },
              ],
            },
          ]
          : []),
        {
          label: t('nav.system', 'SYSTEM'),
          items: [
            { name: t('nav.settings', 'Account Settings'), path: '/settings', icon: Settings },
            { name: t('nav.docs', 'Documentation'), path: '/docs', icon: ScrollText, end: true, prefetch: DOCS_PREFETCH_HANDLERS },
          ],
        },
      ];
    }
    return [
    {
      label: '',
      items: [
        { name: t('nav.dashboard', 'Dashboard'), path: '/app', icon: LayoutDashboard, end: true },
      ],
    },
    ...(manageChrome
      ? [
        {
          label: t('nav.workspace', 'WORKSPACE'),
          items: [
            { name: t('nav.ws_settings', 'Company Profile'), path: '/company-profile', icon: Building, end: true },
            { name: t('nav.properties', 'Properties Directory'), path: '/properties', icon: Building2, end: true },
            { name: t('nav.team', 'Team & Staff'), path: '/workspace/team', icon: Users },
          ],
        },
      ]
      : []),
    ...(hasProperties || isSuperadmin
      ? [
        {
          label: t('nav.property_group', 'PROPERTY'),
          items: [
            ...(manageChrome
              ? [
                { name: t('nav.property_profile', 'Property Profile'), path: '/property/profile', icon: Building2, end: true },
                { name: t('nav.action_center', 'Action Center'), path: '/property/action-center', icon: ShieldCheck, end: true },
                ...(hasModule('vendors') ? [{ name: t('nav.vendors', 'Vendors & Contractors'), path: '/property/vendors', icon: HardHat, end: true }] : []),
                { name: t('nav.expenses_config', 'Property Finances'), path: '/property/expenses/config', icon: Coins, end: true },
              ]
              : []),
            ...(residentExperience
              ? [
                { name: t('nav.my_units', 'Units'), path: '/units', icon: Building },
                ...(residentHasFinancialAccess
                  ? [{ name: t('nav.finances', 'Finances'), path: '/financial-overview', icon: Receipt }]
                  : []),
                ...(residentHasFinancialAccess && hasModule('reports', 'tenant')
                  ? [{ name: t('nav.reports', 'Reports'), path: '/reports', icon: BarChart3, module: 'reports' }]
                  : []),
              ]
              : []),
            ...(residentExperience && hasModule('tenancy_lease', 'tenant')
              && Boolean(activeResidentUnit?.tenancy_enabled)
              ? [{ name: t('nav.tenancy', 'Tenancy'), path: '/tenancy', icon: KeyRound, module: 'tenancy_lease' }]
              : []),
            ...(residentExperience && hasModule('vehicles', 'tenant')
              ? [{ name: t('nav.vehicles', 'Vehicles & Parking'), path: '/my-vehicles', icon: Car, module: 'vehicles' }]
              : []),
            ...(residentExperience && hasModule('documents', 'tenant')
              ? [{ name: t('nav.documents', 'Documents & Records'), path: '/documents', icon: FolderClosed, module: 'documents' }]
              : []),
            ...(residentExperience && hasModule('maintenance', 'tenant')
              ? [{ name: t('nav.maintenance', 'Maintenance'), path: '/maintenance', icon: Wrench, module: 'maintenance' }]
              : []),
            ...(residentExperience && hasModule('vendors', 'tenant')
              ? [{ name: t('nav.vendors', 'Vendors & Contractors'), path: '/property/vendors', icon: HardHat, module: 'vendors' }]
              : []),
          ],
        },
        ...(manageChrome
          ? [{
            label: t('nav.finance', 'FINANCIALS'),
            items: [
              {
                name: t('nav.transactions', 'Transactions'),
                path: '/transactions',
                icon: CreditCard,
              },
              { name: t('nav.balances', 'Financial Overview'), path: '/financial-overview', icon: Users },
              ...(hasModule('reports')
                ? [{ name: t('nav.reports', 'Reports'), path: '/reports', icon: BarChart3, module: 'reports' }]
                : []),
              ...(hasModule('bank_reconciliation') ? [{ name: t('nav.import_statements', 'Import Statements'), path: '/import-statements', icon: Landmark, module: 'bank_reconciliation' }] : []),
            ],
          }]
          : []),

        ...(manageChrome
          ? [{
            label: t('nav.operations', 'OPERATIONS'),
            items: [
              { name: t('nav.units', 'Units'), path: '/units', icon: Building },
              { name: t('nav.residents', 'Residents'), path: '/residents', icon: Users },
              ...(hasModule('tenancy_lease')
                ? [{ name: t('nav.tenancy', 'Tenancy'), path: '/tenancy', icon: KeyRound, module: 'tenancy_lease' }]
                : []),
              ...(hasModule('documents') ? [
                { name: t('nav.documents', 'Documents & Records'), path: '/documents', icon: FolderClosed, module: 'documents' },
              ] : []),
              ...(hasModule('vehicles') ? [
                { name: t('nav.vehicles', 'Vehicles & Parking'), path: '/vehicles', icon: Car, module: 'vehicles' },
              ] : []),
              ...(hasModule('maintenance') ? [{ name: t('nav.maintenance', 'Maintenance'), path: '/maintenance', icon: Wrench, module: 'maintenance' }] : []),
            ],
          }]
          : []),
        ...((residentExperience
          ? (hasModule('amenities', 'tenant') || hasModule('voting', 'tenant'))
          : (hasModule('amenities') || hasModule('voting'))
          )
          ? [{
            label: t('nav.community_group', 'COMMUNITY'),
            items: [
              { name: t('nav.notices', 'Notices'), path: '/notices', icon: Bell },
              ...(residentExperience
                ? [
                  ...(hasModule('amenities', 'tenant') ? [{ name: t('nav.amenities', 'Amenities'), path: '/amenities', icon: CalendarCheck, module: 'amenities' }] : []),
                  ...(hasModule('voting', 'tenant') && residentCanVote ? [{ name: t('nav.polls', 'Polls'), path: '/polls', icon: Vote, module: 'voting' }] : []),
                ]
                : [
                  ...(hasModule('amenities') ? [{ name: t('nav.amenities', 'Amenities'), path: '/amenities', icon: CalendarCheck, module: 'amenities' }] : []),
                  ...(hasModule('voting') ? [{ name: t('nav.polls', 'Polls'), path: '/polls', icon: Vote, module: 'voting' }] : []),
                ]
              ),
            ],
          }]
          : []),
      ]
      : []),
    {
      label: t('nav.system', 'SYSTEM'),
      items: [
        { name: t('nav.settings', 'Account Settings'), path: '/settings', icon: Settings },
        { name: t('nav.docs', 'Documentation'), path: '/docs', icon: ScrollText, end: true, prefetch: DOCS_PREFETCH_HANDLERS },
      ],
    },
  ];
}, [hasModule, hasProperties, isSuperadmin, manageChrome, residentExperience, residentHasFinancialAccess, residentCanVote, activeResidentUnit, isContractor, isContractorAdmin, t]);

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('koinon_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('koinon_sidebar_collapsed', String(next));
      } catch {
        // ignore localStorage errors
      }
      return next;
    });
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('koinon_collapsed_nav_groups');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // Prefetch the palette chunk once the app is idle: it stays out of the entry
  // bundle, but Cmd+K still opens instantly instead of waiting on a fetch.
  useEffect(() => {
    if (typeof window.requestIdleCallback !== 'function') return;
    const idle = window.requestIdleCallback(() => {
      void import('@/components/common/CommandPalette');
    });
    return () => window.cancelIdleCallback(idle);
  }, []);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      localStorage.setItem('koinon_collapsed_nav_groups', JSON.stringify([...next]));
      return next;
    });
  };

  const scopeDropdownRef = useRef<HTMLDivElement>(null);
  const unitDropdownRef = useRef<HTMLDivElement>(null);

  const {
    activeIndex: activeScopeIndex,
    onTriggerKeyDown: onScopeTriggerKeyDown,
    onMenuKeyDown: onScopeMenuKeyDown,
  } = useListboxKeyboard({
    itemCount: properties.length,
    isOpen: scopeDropdownOpen,
    setIsOpen: setScopeDropdownOpen,
    selectedIndex: properties.findIndex((p: { id: string }) => p.id === selectedPropertyId),
    onSelect: (i) => {
      const next = properties[i];
      if (next) setSelectedPropertyId(next.id);
    },
  });

  const selectedUnitIndex = scopedUnits.findIndex((u) => u.unit_id === activeManageUnit?.unit_id);
  const {
    activeIndex: activeUnitIndex,
    onTriggerKeyDown: onUnitTriggerKeyDown,
    onMenuKeyDown: onUnitMenuKeyDown,
  } = useListboxKeyboard({
    itemCount: scopedUnits.length,
    isOpen: unitDropdownOpen,
    setIsOpen: setUnitDropdownOpen,
    selectedIndex: selectedUnitIndex,
    onSelect: (i) => {
      const next = scopedUnits[i];
      if (next) setSelectedUnitId?.(next.unit_id);
    },
  });

  // Outside-click dismissal for the scope and unit menus.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(event.target as Node)) {
        setScopeDropdownOpen(false);
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setUnitDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile drawer and dropdowns on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setSwitcherOpen(false);
    setScopeDropdownOpen(false);
    setUnitDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const { current: currentLang, changeLanguage } = useLanguage();

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const renderSidebarContent = (isCollapsed: boolean, isMobile: boolean) => (
    <div
      className={cn(
        'flex flex-col h-full bg-sidebar border-r border-sidebar-border text-sidebar-foreground select-none',
        isCollapsed ? 'items-center' : ''
      )}
    >
      {/* 1. Sidebar Header (Koinon Standard) */}
      {isCollapsed ? (
        <div className="flex h-16 min-h-16 w-full items-center justify-center px-2.5 border-b border-sidebar-border/80 shrink-0">
          <Link
            to={isContractor ? '/contractor/dashboard' : '/app'}
            className="flex items-center justify-center size-8 rounded-lg bg-primary text-primary-foreground shadow-xs shrink-0 transition-opacity hover:opacity-90"
            title="Koinon"
            aria-label="Koinon"
          >
            <Building2 className="w-4.5 h-4.5" />
          </Link>
        </div>
      ) : (
        <div className="flex h-16 min-h-16 items-center justify-between px-3.5 border-b border-sidebar-border/80 shrink-0">
          <Link
            to={isContractor ? '/contractor/dashboard' : '/app'}
            className="flex items-center gap-2.5 min-w-0 transition-opacity hover:opacity-90"
            onClick={() => isMobile && setMobileMenuOpen(false)}
          >
            <span className="flex items-center justify-center size-8 rounded-lg bg-primary text-primary-foreground shadow-xs shrink-0">
              <Building2 className="w-4.5 h-4.5" />
            </span>
            <div className="flex items-baseline gap-1.5 truncate">
              <span className="text-sm font-bold tracking-tight text-sidebar-foreground truncate">
                Koinon
              </span>
              <span className="text-xs text-sidebar-muted/70 font-medium">Κοινόν</span>
            </div>
          </Link>

          {/* Quick Toggles (Privacy Mode, Theme Mode, Mobile Close) */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={togglePrivacyMode}
              className="text-sidebar-muted hover:text-sidebar-foreground transition-colors p-1.5 rounded-lg hover:bg-sidebar-accent cursor-pointer"
              title={privacyMode ? t('shell.show_balances') : t('shell.hide_balances')}
            >
              {privacyMode ? <EyeOff size={15} className="text-amber-500" /> : <Eye size={15} />}
            </button>

            <button
              type="button"
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="text-sidebar-muted hover:text-sidebar-foreground transition-colors p-1.5 rounded-lg hover:bg-sidebar-accent cursor-pointer"
              title={isDark ? t('shell.theme_light') : t('shell.theme_dark')}
            >
              {isDark ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {isMobile && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="lg:hidden text-sidebar-muted hover:text-sidebar-foreground p-1.5 rounded-lg hover:bg-sidebar-accent ml-0.5 cursor-pointer"
                aria-label={t('shell.close_navigation', 'Close navigation')}
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* 2. Command Palette Search Bar (Koinon Standard) */}
      {isCollapsed ? (
        <div className="px-2 pt-3 pb-1 shrink-0 flex justify-center w-full">
          <Tooltip content={`${t('shell.search_everything', 'Search everything...')} (Ctrl+K)`} side="right">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-sidebar-border/80 bg-sidebar-accent/40 text-sidebar-muted transition-all hover:bg-sidebar-accent hover:text-sidebar-foreground hover:border-sidebar-border cursor-pointer"
              aria-label={t('shell.search_everything', 'Search everything...')}
            >
              <Search size={15} />
            </button>
          </Tooltip>
        </div>
      ) : (
        <div className="px-3 pt-3 pb-1 shrink-0">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="group flex w-full items-center gap-2 rounded-lg border border-sidebar-border/80 bg-sidebar-accent/40 px-3 py-2 text-[12.5px] text-sidebar-muted transition-all hover:bg-sidebar-accent hover:text-sidebar-foreground hover:border-sidebar-border cursor-pointer"
          >
            <Search size={13} className="shrink-0 text-sidebar-muted group-hover:text-primary" />
            <span className="flex-1 text-left truncate">
              {t('shell.search_everything', 'Search everything...')}
            </span>
            <kbd className="hidden lg:inline-flex h-[17px] items-center rounded border border-sidebar-border bg-sidebar px-1 font-mono text-[9.5px] font-semibold text-sidebar-muted/80">
              Ctrl K
            </kbd>
          </button>
        </div>
      )}

      {/* 3. Navigation Links (Koinon Standard) */}
      <div
        className={cn(
          'flex-1 min-h-0 overflow-y-auto pb-3',
          isCollapsed ? 'w-full px-2 pt-2 space-y-2' : 'px-3 pt-2 space-y-4'
        )}
      >
        {navigationGroups.map((group, groupIdx) => {
          if (isCollapsed) {
            return (
              <div key={group.label || `group-${groupIdx}`} className="space-y-1">
                {groupIdx > 0 && group.items.length > 0 && (
                  <div className="my-2 border-t border-sidebar-border/60 mx-1.5" />
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <Tooltip
                      key={item.path}
                      side="right"
                      content={
                        <div className="flex items-center gap-1.5 font-medium">
                          <span>{item.name}</span>
                          {item.badge && (
                            <Badge size="xs" variant="purple">
                              {item.badge}
                            </Badge>
                          )}
                        </div>
                      }
                    >
                      <NavLink
                        to={item.path}
                        end={item.end}
                        onClick={() => isMobile && setMobileMenuOpen(false)}
                        {...item.prefetch}
                        className={({ isActive }) =>
                          cn(
                            'relative flex h-10 w-10 mx-auto items-center justify-center rounded-lg transition-all',
                            isActive
                              ? 'bg-primary/10 text-primary font-semibold shadow-xs'
                              : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground'
                          )
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              size={18}
                              className={cn(
                                'shrink-0',
                                isActive ? 'text-primary' : 'text-sidebar-muted'
                              )}
                            />
                            {isActive && (
                              <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-primary" />
                            )}
                            {item.badge && (
                              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-primary" />
                            )}
                            <span className="sr-only">{item.name}</span>
                          </>
                        )}
                      </NavLink>
                    </Tooltip>
                  ))}
                </div>
              </div>
            );
          }

          const isCollapsedGroup = group.label ? collapsedGroups.has(group.label) : false;
          return (
            <div key={group.label} className="space-y-1">
              {group.label && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-3 text-[10px] uppercase tracking-[0.12em] font-semibold text-sidebar-muted/50 hover:text-sidebar-muted transition-colors cursor-pointer group/header"
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    size={12}
                    className={cn(
                      'transition-transform duration-200',
                      isCollapsedGroup && '-rotate-90'
                    )}
                  />
                </button>
              )}
              <div className={cn('space-y-0.5', group.label && isCollapsedGroup && 'hidden')}>
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.end}
                    onClick={() => isMobile && setMobileMenuOpen(false)}
                    {...item.prefetch}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 text-[13px] font-medium transition-all rounded-lg px-3 py-2',
                        isActive
                          ? 'bg-primary/[0.08] text-primary border-l-[3px] border-primary pl-[9px]'
                          : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          size={17}
                          className={cn(
                            'shrink-0',
                            isActive ? 'text-primary' : 'text-sidebar-muted'
                          )}
                        />
                        <span className="truncate flex-1">{item.name}</span>
                        {item.badge && (
                          <Badge size="sm" variant="purple">
                            {item.badge}
                          </Badge>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* 4. Bottom User Profile Trigger */}
      <div
        className={cn(
          'border-t border-sidebar-border mt-auto shrink-0 relative',
          isCollapsed ? 'p-2.5 flex flex-col items-center w-full' : 'p-3'
        )}
      >
        {isCollapsed ? (
          <Tooltip content={user?.full_name || user?.email || t('shell.my_account')} side="right" disabled={switcherOpen}>
            <button
              type="button"
              onClick={() => setSwitcherOpen((prev) => !prev)}
              className={cn(
                'h-9 w-9 rounded-lg bg-gradient-to-tr from-primary to-indigo-500 text-white font-bold text-xs flex items-center justify-center shadow-xs cursor-pointer hover:ring-2 hover:ring-primary/40 transition-all',
                switcherOpen && 'ring-2 ring-primary shadow-sm'
              )}
              aria-expanded={switcherOpen}
              aria-haspopup="true"
              aria-label={t('shell.my_account')}
            >
              {getInitials(user?.full_name || user?.email)}
            </button>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={() => setSwitcherOpen((prev) => !prev)}
            className={cn(
              'flex items-center gap-3 w-full rounded-xl px-2.5 py-2 text-sm hover:bg-sidebar-accent transition-all text-left cursor-pointer border border-transparent',
              switcherOpen && 'bg-sidebar-accent border-sidebar-border/60 shadow-xs'
            )}
            aria-expanded={switcherOpen}
            aria-haspopup="true"
          >
            <div className="h-8 w-8 rounded-lg bg-gradient-to-tr from-primary to-indigo-500 text-white font-bold text-xs flex items-center justify-center shadow-xs shrink-0">
              {getInitials(user?.full_name || user?.email)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-sidebar-foreground truncate">
                {user?.full_name || user?.email?.split('@')[0] || t('shell.my_account')}
              </p>
              <p className="text-[10px] text-sidebar-muted/70 truncate">
                {user?.email || t('shell.my_account')}
              </p>
            </div>
            <ChevronsUpDown size={13} className="text-sidebar-muted/60 shrink-0" />
          </button>
        )}

        {/* User Profile Popover */}
        {switcherOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setSwitcherOpen(false)}
            />

            <div
              className={cn(
                'z-50 rounded-2xl bg-popover text-popover-foreground border border-border shadow-2xl p-2.5 space-y-2 animate-in fade-in zoom-in-95 duration-150',
                isCollapsed
                  ? 'absolute bottom-2 left-[74px] w-64'
                  : 'absolute bottom-16 left-3 right-3'
              )}
            >
              {/* User Identity Header Card */}
              <div className="flex items-center gap-2.5 p-2 rounded-xl bg-secondary/50 border border-border/50">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-tr from-primary to-indigo-500 text-white font-bold text-xs flex items-center justify-center shadow-xs shrink-0">
                  {getInitials(user?.full_name || user?.email)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-foreground truncate">
                    {user?.full_name || t('shell.my_account')}
                  </p>
                  <p className="text-[10.5px] text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* App Mode Switcher */}
              {user?.available_modes && user.available_modes.length > 1 && (
                <>
                  <div className="space-y-0.5 pt-0.5">
                    <div className="px-2 pb-0.5 pt-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        {t('shell.app_mode', 'App Mode')}
                      </span>
                    </div>
                    {user.available_modes.includes('superadmin') && (
                      <button
                        type="button"
                        onClick={() => {
                          setAppMode('superadmin');
                          setSwitcherOpen(false);
                          navigate('/superadmin');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer',
                          appMode === 'superadmin' ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                        )}
                      >
                        <ShieldCheck size={14} className={appMode === 'superadmin' ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'} />
                        <span>{t('shell.mode_superadmin', 'Superadmin')}</span>
                        {appMode === 'superadmin' && <Check size={14} className="ml-auto" />}
                      </button>
                    )}
                    {user.available_modes.includes('manage') && (
                      <button
                        type="button"
                        onClick={() => {
                          setAppMode('manage');
                          setResidentMode('manage');
                          setSwitcherOpen(false);
                          navigate('/app');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer',
                          appMode === 'manage' ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                        )}
                      >
                        <Building2 size={14} className={appMode === 'manage' ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'} />
                        <span>{t('shell.mode_manage', 'Property Management')}</span>
                        {appMode === 'manage' && <Check size={14} className="ml-auto" />}
                      </button>
                    )}
                    {user.available_modes.includes('home') && (
                      <button
                        type="button"
                        onClick={() => {
                          setAppMode('home');
                          setResidentMode('home');
                          setSwitcherOpen(false);
                          navigate('/units');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer',
                          appMode === 'home' ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                        )}
                      >
                        <Home size={14} className={appMode === 'home' ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'} />
                        <span>{t('shell.mode_home', 'Owner Portal')}</span>
                        {appMode === 'home' && <Check size={14} className="ml-auto" />}
                      </button>
                    )}
                    {user.available_modes.includes('contractor') && (
                      <button
                        type="button"
                        onClick={() => {
                          setAppMode('contractor');
                          setSwitcherOpen(false);
                          navigate('/contractor/dashboard');
                        }}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-left cursor-pointer',
                          appMode === 'contractor' ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-secondary'
                        )}
                      >
                        <HardHat size={14} className={appMode === 'contractor' ? 'text-primary shrink-0' : 'text-muted-foreground shrink-0'} />
                        <span>{t('shell.mode_contractor', 'Contractor Portal')}</span>
                        {appMode === 'contractor' && <Check size={14} className="ml-auto" />}
                      </button>
                    )}
                  </div>
                  <div className="border-t border-border mt-1" />
                </>
              )}

              {/* Account Navigation Links */}
              <div className="space-y-0.5 pt-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setSwitcherOpen(false);
                    navigate('/settings?tab=profile');
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-secondary transition-colors text-left cursor-pointer"
                >
                  <UserCircle size={14} className="text-primary shrink-0" />
                  <span>{t('nav.profile', 'My Profile & Details')}</span>
                </button>

                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      setSwitcherOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-secondary transition-colors text-left cursor-pointer"
                  >
                    <Settings size={14} className="text-primary shrink-0" />
                    <span>{t('nav.settings', 'Settings & Backups')}</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setSwitcherOpen(false);
                    navigate('/settings/security');
                  }}
                  className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-foreground hover:bg-secondary transition-colors text-left cursor-pointer"
                >
                  <ShieldCheck size={14} className="text-primary shrink-0" />
                  <span>{t('shell.security_2fa', 'Security & 2FA')}</span>
                  {user?.is_2fa_enabled && (
                    <span className="ml-auto text-[9px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded">
                      {t('shell.badge_2fa_active', '2FA Active')}
                    </span>
                  )}
                </button>
              </div>

              <div className="border-t border-border" />

              {/* Preferences: Language & Theme */}
              <div className="space-y-1.5 pt-0.5">
                {/* Language Switcher */}
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
                    <Languages size={12} />
                    {t('shell.language_label', 'Language')}
                  </span>
                  <div className="flex items-center bg-secondary p-0.5 rounded-lg border border-border/60">
                    <button
                      type="button"
                      onClick={() => changeLanguage('en')}
                      className={cn(
                        'px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer',
                        currentLang.startsWith('en')
                          ? 'bg-card text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      EN
                    </button>
                    <button
                      type="button"
                      onClick={() => changeLanguage('el')}
                      className={cn(
                        'px-2 py-0.5 rounded-md text-[11px] font-semibold transition-colors cursor-pointer',
                        currentLang.startsWith('el')
                          ? 'bg-card text-foreground shadow-xs'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      ΕΛ
                    </button>
                  </div>
                </div>

                {/* Theme Switcher */}
                <div className="flex items-center justify-between px-1">
                  <span className="text-[11px] font-medium text-muted-foreground flex items-center gap-1.5">
                    {isDark ? <Moon size={12} /> : <Sun size={12} />}
                    {t('shell.theme_label', 'Theme')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setTheme(isDark ? 'light' : 'dark')}
                    className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-secondary hover:bg-secondary/80 text-[11px] font-medium text-foreground transition-colors cursor-pointer border border-border/60"
                  >
                    {isDark ? (
                      <>
                        <Moon size={11} className="text-primary" />
                        <span>{t('shell.theme_dark_name', 'Dark')}</span>
                      </>
                    ) : (
                      <>
                        <Sun size={11} className="text-amber-500" />
                        <span>{t('shell.theme_light_name', 'Light')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Logout Button */}
              <button
                type="button"
                onClick={() => {
                  setSwitcherOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors text-left cursor-pointer"
              >
                <LogOut size={13} />
                <span>{t('nav.logout', 'Sign Out')}</span>
              </button>
            </div>
          </>
        )}

        {/* 5. Version label */}
        {!isCollapsed && (
          <p className="text-[10px] text-center text-sidebar-muted/40 font-mono mt-2">
            Version v{pkg.version}
          </p>
        )}
      </div>
    </div>
  );

  // Multi-hat accounts (e.g. contractor + resident) resolve their landing mode
  // asynchronously in the auth provider. Hold a skeleton until then so the
  // first paint never flashes the wrong menu.
  if (!appModeResolved && (user?.available_modes?.length ?? 0) > 1) {
    return (
      <div
        className="flex min-h-screen bg-background"
        aria-busy="true"
        aria-label={t('common.loading', { defaultValue: 'Loading...' })}
      >
        <div className="hidden lg:block w-64 border-r border-sidebar-border bg-sidebar p-3 space-y-2 animate-pulse">
          <div className="h-8 rounded-lg bg-sidebar-accent" />
          <div className="h-10 rounded-lg bg-sidebar-accent/60" />
          <div className="h-10 rounded-lg bg-sidebar-accent/60" />
          <div className="h-10 rounded-lg bg-sidebar-accent/60" />
        </div>
        <div className="flex-1 p-6 max-w-7xl mx-auto w-full space-y-4 animate-pulse">
          <div className="h-8 w-48 rounded-lg bg-secondary" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="h-24 rounded-xl bg-card border border-border/80" />
            <div className="h-24 rounded-xl bg-card border border-border/80" />
            <div className="h-24 rounded-xl bg-card border border-border/80" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground antialiased selection:bg-primary selection:text-white">
      <div className="flex-1 flex">
        {/* Mobile Drawer Backdrop */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Drawer Navigation */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border transform transition-transform duration-200 ease-in-out lg:hidden shadow-2xl',
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {renderSidebarContent(false, true)}
        </aside>

        {/* Desktop Persistent Sidebar */}
        <aside
          className={cn(
            'hidden lg:flex border-r border-sidebar-border bg-sidebar flex-col shrink-0 fixed inset-y-0 left-0 z-30 transition-[width] duration-200 ease-in-out',
            isSidebarCollapsed ? 'w-[68px]' : 'w-64'
          )}
        >
          {/* Border Edge Collapse/Expand Button (Floating < / > pill matching user design) */}
          <button
            type="button"
            onClick={toggleSidebar}
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-40 flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-card text-sidebar-muted shadow-xs hover:bg-sidebar-accent hover:text-sidebar-foreground hover:shadow-sm transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            title={
              isSidebarCollapsed
                ? `${t('shell.expand_sidebar', 'Expand sidebar')} (Ctrl+B)`
                : `${t('shell.collapse_sidebar', 'Collapse sidebar')} (Ctrl+B)`
            }
            aria-label={
              isSidebarCollapsed
                ? t('shell.expand_sidebar', 'Expand sidebar')
                : t('shell.collapse_sidebar', 'Collapse sidebar')
            }
          >
            {isSidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>

          {renderSidebarContent(isSidebarCollapsed, false)}
        </aside>

        {/* Main Content Area */}
        <div
          className={cn(
            'flex-1 flex flex-col min-w-0 transition-[padding] duration-200 ease-in-out',
            isSidebarCollapsed ? 'lg:pl-[68px]' : 'lg:pl-64'
          )}
        >
          {/* Pinned Header & Banner Area */}
          <div className="sticky top-0 z-20 bg-background">
            <ImpersonationBanner />
            <BroadcastBanner />
            <TrialBanner />
            {isReadOnly && (
              <WorkspaceSuspendedBanner
                workspaceName={activeMembership?.workspace_name || selectedProperty?.name}
                pmLabel={selectedProperty?.management_company || activeMembership?.workspace_name}
              />
            )}

            {/* Scope Header Bar */}
            <div
              className={cn(
                'border-b border-border bg-background px-6',
                (!hasDesktopScopeControls || isDashboard) && 'lg:hidden'
              )}
            >
              <div className="flex h-12 max-w-7xl items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                {/* Mobile menu hamburger */}
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="lg:hidden p-1 text-sidebar-muted hover:text-sidebar-foreground transition-colors cursor-pointer"
                  aria-label={t('shell.open_nav_menu')}
                >
                  <Menu size={18} />
                </button>

                {/* Residents (incl. dual-hat in My-home mode) get their own
                    property switcher (multi-PM properties); staff get the
                    workspace + property chrome. */}
                {residentExperience && <ResidentScopeSwitcher />}

                {/* Organization + property scope controls are management chrome: */}
                {/* the resident experience never sees the PM's pickers. */}
                {!isContractor && !residentExperience && (
                  <>
                    {/* Organization Switcher */}
                    <WorkspaceSwitcher />

                    <span className="text-[11px] font-medium tracking-[0.1em] text-muted-foreground/70 hidden sm:inline">
                      {t('common.property_upper', 'BUILDING:')}
                    </span>

                    {/* Scoping pill button */}
                    <div className="relative" ref={scopeDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setScopeDropdownOpen((prev) => !prev)}
                        onKeyDown={onScopeTriggerKeyDown}
                        aria-haspopup="listbox"
                        aria-expanded={scopeDropdownOpen}
                        className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary cursor-pointer"
                      >
                        <Building2 size={13} className="text-primary shrink-0" />
                        <span className="max-w-[140px] sm:max-w-[200px] truncate">
                          {selectedProperty?.name || (properties.length === 0 ? t('shell.no_properties') : t('shell.select_property'))}
                        </span>
                        <ChevronDown
                          size={12}
                          className={cn(
                            'text-muted-foreground transition-transform duration-200 shrink-0',
                            scopeDropdownOpen && 'rotate-180'
                          )}
                        />
                      </button>

                      {/* Scope dropdown popover */}
                      {scopeDropdownOpen && (
                        <div className="absolute left-0 mt-1.5 w-64 rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                          <div className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                            {activeMembership
                              ? t('shell.workspace_properties', {
                                  name: activeMembership.workspace_name,
                                })
                              : t('shell.select_scope')}
                          </div>
                          <div
                            role="listbox"
                            tabIndex={-1}
                            aria-activedescendant={
                              activeScopeIndex >= 0 ? `scope-option-${activeScopeIndex}` : undefined
                            }
                            onKeyDown={onScopeMenuKeyDown}
                            className="max-h-48 overflow-y-auto space-y-0.5 py-1"
                          >
                            {properties.length === 0 ? (
                              <div className="px-2.5 py-3 text-center text-xs text-muted-foreground">
                                No properties in this workspace yet.
                              </div>
                            ) : (
                              properties.map((b: any, i: number) => {
                                const isSelected = b.id === selectedPropertyId;
                                const isActive = i === activeScopeIndex;
                                return (
                                  <button
                                    key={b.id}
                                    id={`scope-option-${i}`}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={() => {
                                      setSelectedPropertyId(b.id);
                                      setScopeDropdownOpen(false);
                                    }}
                                    className={cn(
                                      'w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer text-left',
                                      isActive && !isSelected ? 'bg-secondary text-foreground' : '',
                                      isSelected
                                        ? 'bg-primary/10 text-primary font-semibold'
                                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                    )}
                                  >
                                    <div className="min-w-0 pr-2">
                                      <p className="font-semibold text-foreground truncate flex items-center gap-1.5">
                                        {b.name}
                                        {b.status === 'suspended' && (
                                          <Badge size="sm" variant="warning">
                                            Suspended
                                          </Badge>
                                        )}
                                      </p>
                                      <p className="text-[10.5px] text-muted-foreground truncate">
                                        {b.city || b.address}
                                      </p>
                                    </div>
                                    {isSelected && (
                                      <Check size={13} className="text-primary shrink-0" />
                                    )}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          <div className="border-t border-border pt-1">
                            <button
                              onClick={() => {
                                setScopeDropdownOpen(false);
                                navigate('/properties');
                              }}
                              className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                            >
                              <Plus size={13} /> {t('nav.properties', 'Properties Directory')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Unit Switcher when on /tenancy and units are available */}
                    {isTenancyRoute && scopedUnits.length > 0 && (
                      <>
                        <span className="text-[11px] font-medium tracking-[0.1em] text-muted-foreground/70 hidden sm:inline ml-1">
                          {t('suspension.scope_unit', 'UNIT:')}
                        </span>

                        <div className="relative" ref={unitDropdownRef}>
                          <button
                            type="button"
                            onClick={() => {
                              if (scopedUnits.length > 1) {
                                setUnitDropdownOpen((prev) => !prev);
                              }
                            }}
                            onKeyDown={scopedUnits.length > 1 ? onUnitTriggerKeyDown : undefined}
                            aria-haspopup={scopedUnits.length > 1 ? 'listbox' : undefined}
                            aria-expanded={scopedUnits.length > 1 ? unitDropdownOpen : undefined}
                            aria-label={t('suspension.select_unit', 'Select unit')}
                            className="group inline-flex items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary cursor-pointer"
                          >
                            <Home size={13} className="text-primary shrink-0" />
                            <span className="max-w-[120px] sm:max-w-[180px] truncate font-semibold">
                              {activeManageUnit?.unit_number ? `Unit ${activeManageUnit.unit_number}` : t('suspension.select_unit', 'Select unit')}
                            </span>
                            {scopedUnits.length > 1 && (
                              <ChevronDown
                                size={12}
                                className={cn(
                                  'text-muted-foreground transition-transform duration-200 shrink-0',
                                  unitDropdownOpen && 'rotate-180'
                                )}
                              />
                            )}
                          </button>

                          {unitDropdownOpen && scopedUnits.length > 1 && (
                            <div className="absolute left-0 mt-1.5 w-64 rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150">
                              <div className="px-2 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground/70">
                                {t('suspension.scope_unit', 'UNIT:')}
                              </div>
                              <div
                                role="listbox"
                                tabIndex={-1}
                                aria-activedescendant={
                                  activeUnitIndex >= 0 ? `manage-unit-option-${activeUnitIndex}` : undefined
                                }
                                onKeyDown={onUnitMenuKeyDown}
                                className="max-h-48 overflow-y-auto space-y-0.5 py-1"
                              >
                                {scopedUnits.map((u, i) => {
                                  const isSelected = u.unit_id === activeManageUnit?.unit_id;
                                  const isActive = i === activeUnitIndex;
                                  return (
                                    <button
                                      key={u.unit_id}
                                      id={`manage-unit-option-${i}`}
                                      type="button"
                                      role="option"
                                      aria-selected={isSelected}
                                      onClick={() => {
                                        setSelectedUnitId?.(u.unit_id);
                                        setUnitDropdownOpen(false);
                                      }}
                                      className={cn(
                                        'w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-colors cursor-pointer text-left',
                                        isActive && !isSelected ? 'bg-secondary text-foreground' : '',
                                        isSelected
                                          ? 'bg-primary/10 text-primary font-semibold'
                                          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                                      )}
                                    >
                                      <span className="font-semibold text-foreground truncate">
                                        Unit {u.unit_number}
                                      </span>
                                      {isSelected && <Check size={13} className="text-primary shrink-0" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Right side area */}


            </div>
          </div>
        </div>

          {/* Dynamic Route Content */}
          <main className="flex-1 p-6 max-w-7xl w-full mx-auto">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Global Command Palette */}
      {searchOpen && (
        <Suspense fallback={null}>
          <CommandPalette isOpen={searchOpen} onClose={closeSearch} />
        </Suspense>
      )}
    </div>
  );

};
